import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import toast from "react-hot-toast";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import useApi from "components/Utils/api";

const SECTIONS = [
  { key: "local", label: "Local cards" },
  { key: "foreign", label: "Foreign cards" },
];
const PARTIES = [
  { key: "Hnb", label: "HNB %" },
  { key: "Dms", label: "DMS %" },
  { key: "Yc", label: "YouCloud %" },
  { key: "Mf", label: "Merchant Final %" },
];

const RATE_FIELDS = SECTIONS.flatMap((s) => PARTIES.map((p) => `${s.key}${p.key}`));
const emptyRates = () => RATE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: "" }), {});

function MdrQrModal({ open, onClose, qrData, getQr }) {
  const api = useApi();
  const [rates, setRates] = useState(emptyRates());

  useEffect(() => {
    if (!open) return;
    if (qrData) {
      setRates(
        RATE_FIELDS.reduce(
          (acc, f) => ({ ...acc, [f]: qrData[f] === null || qrData[f] === undefined ? "" : qrData[f] }),
          {}
        )
      );
    } else {
      setRates(emptyRates());
    }
  }, [open, qrData]);

  const validate = () => {
    for (let i = 0; i < RATE_FIELDS.length; i += 1) {
      const f = RATE_FIELDS[i];
      if (rates[f] === "" || isNaN(parseFloat(rates[f]))) {
        toast.error("Please enter all rate values");
        return false;
      }
    }
    return true;
  };

  const handleSave = () => {
    if (!validate()) return;
    const body = {};
    RATE_FIELDS.forEach((f) => {
      body[f] = rates[f] === "" ? null : parseFloat(rates[f]);
    });

    api
      .post("master/qr/save", body)
      .then((response) => {
        if (response.data && response.data.responseCode === 200) {
          toast.success("QR MDR rates saved");
          onClose();
          getQr();
        } else {
          toast.error((response.data && response.data.responseMessage) || "Failed to save QR MDR");
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        toast.error("Failed to save QR MDR");
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{qrData ? "Edit QR MDR" : "Add QR MDR"} (QR)</DialogTitle>
      <DialogContent>
        <MDBox mt={1}>
          <MDTypography variant="caption" color="text">
            QR MDR rates apply uniformly to all merchants.
          </MDTypography>
          {SECTIONS.map((section) => (
            <MDBox key={section.key} mt={3}>
              <MDTypography variant="button" fontWeight="bold" textTransform="uppercase">
                {section.key} — {section.label}
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                {PARTIES.map((party) => {
                  const field = `${section.key}${party.key}`;
                  return (
                    <Grid item xs={6} md={3} key={field}>
                      <TextField
                        fullWidth
                        type="number"
                        inputProps={{ step: "0.001" }}
                        label={party.label}
                        value={rates[field]}
                        onChange={(e) => setRates({ ...rates, [field]: e.target.value })}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </MDBox>
          ))}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton color="secondary" onClick={onClose}>
          Cancel
        </MDButton>
        <MDButton color="info" variant="gradient" onClick={handleSave}>
          Save Changes
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

MdrQrModal.defaultProps = {
  open: false,
  onClose: () => {},
  qrData: null,
  getQr: () => {},
};

MdrQrModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  qrData: PropTypes.object,
  getQr: PropTypes.func,
};

export default MdrQrModal;
